/**
 * @todo convert components/card into module and use inline js to load this module
 * @component components/cards/course
 * @description Register countdowns, ajax event, etc. for course cards.
 */

import {CountdownClock} from '../../modules/countdownClock.js';

/**
 * Observers countdown timer and disables course card when the course is being started.
 * @param {HTMLElement} card
 */
function initCountdown(card) {
    /** @type {HTMLElement} */
    const countdownElement = card.querySelector('.clock');

    if (countdownElement) {
        // Skip JavaScript countdown initialization if static display is requested
        // Check for data-static-display on the first child (the actual countdown component)
        const countdownContent = countdownElement.firstElementChild;
        if (countdownContent && countdownContent.getAttribute('data-static-display') === 'true') {
            return;
        }

        new CountdownClock(countdownElement, Number(card.dataset.closeTimestamp), () => {
            const viewCourseButton = card.querySelector('.js-countdownDependentCardState');
            viewCourseButton.textContent = card.dataset.closedText;
            card.classList.add('card--disabled');
        });
    }
}

/**
 * @param {HTMLElement} card
 */
function initProgressOnViewport(card) {
    /** @type {?HTMLElement} */
    const circularProgress = card.querySelector('.js-progressCircular');
    if (!circularProgress) return;

    const percentage = Number(circularProgress.dataset.percentage);

    const observer = new IntersectionObserver(
        (entries, observer) => {
            entries.forEach((circularProgressObserverEntry) => {
                if (circularProgressObserverEntry.intersectionRatio >= 1) {
                    const target = /** @type {HTMLElement} */ (circularProgressObserverEntry.target);
                    updateProgress(target, percentage);
                    observer.unobserve(circularProgressObserverEntry.target);
                }
            });
        },
        {
            threshold: 1.0,
        }
    );
    observer.observe(circularProgress);
}

/**
 * @param {HTMLElement} progressRing
 * @param {number} percentage
 */
function updateProgress(progressRing, percentage) {
    const filledOutIndicator = progressRing.querySelector('.js-filledOutIndicator');
    const radius = Number(filledOutIndicator.getAttribute('r'));
    const circumference = Math.PI * (radius * 2);
    const strokeDashoffset = ((100 - percentage) / 100) * circumference;

    if (percentage === 100) {
        progressRing.classList.add('progressCircular--complete');
    }

    filledOutIndicator.setAttribute('style', `stroke-dashoffset: ${strokeDashoffset}px`);
}

/**
 * Initialize card countdown and stroke fill %
 * @param {HTMLElement} card
 */
export function initCardAnimations(card) {
    initCountdown(card);
    initProgressOnViewport(card);
}

/**
 * Initialize component, register all event handlers
 */
function initializeComponent() {
    const courseCards = [...document.querySelectorAll('.courseCard[data-course-id]')];
    courseCards.forEach(
        /** @param {HTMLElement} card */
        (card) => {
            initCardAnimations(card);
        }
    );
}

// init component when document is ready
document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initializeComponent)
    : initializeComponent();
